import { useUserEmailIfLoggedIn } from '@/auth/authentication';
import {
    APOLLO_CACHE_CONFIG,
    APOLLO_LINK,
    AXLE_ERROR_HANDLING_LINK,
} from '@/fetching/graphql/GraphQLClient';
import { REFETCH_AFTER_MUTATION_LINK } from '@/fetching/graphql/refetchAfterMutationLink';
import { isTest } from '@/utils/axleEnvironment';
import { ApolloClient, ApolloLink, InMemoryCache, RefetchEventManager } from '@apollo/client';
import { ApolloProvider as Provider, useApolloClient } from '@apollo/client/react';
import { FC, ReactNode, useCallback, useMemo, useRef } from 'react';

export const useResetStore = () => {
    const client = useApolloClient();

    return useCallback(() => {
        client.stop();
        return client.resetStore();
    }, [client]);
};

const createClient = (_user: unknown) => {
    const cache = new InMemoryCache(APOLLO_CACHE_CONFIG);
    // Automatically refetch active queries when the tab regains focus or the
    // browser comes back online, so the dashboard refreshes stale data without a
    // manual reload. With no per-query `refetchOn`, all active queries opt in by
    // default; the manager is torn down via `client.stop()` on client recreation.
    //
    // Disabled in the e2e build (FE-717): under headless Playwright, focus/online
    // events fire spuriously and refetch every active query — including the
    // `useSuspenseQuery` queries inside open dialogs. A refetch that supersedes an
    // in-flight suspense query errors and unmounts the dialog mid-test, so waits on
    // dialog content time out. The suite doesn't exercise focus-refetch anyway.
    const refetchEventManager = isTest
        ? undefined
        : new RefetchEventManager({
              sources: {
                  // Disabled due to instability on refocus/network change
                  // https://axlehealth.slack.com/archives/C08EARU2SBU/p1782503159765919
                  // windowFocus: windowFocusSource,
                  // online: onlineSource,
              },
          });
    return new ApolloClient({
        cache,
        link: ApolloLink.from([REFETCH_AFTER_MUTATION_LINK, AXLE_ERROR_HANDLING_LINK, APOLLO_LINK]),
        refetchEventManager,
        // Apollo Client 4 flipped the default to `true`, which emits extra loading
        // states (e.g. on refetch). Keep the v3 behavior to avoid spinner flicker;
        // revisit adopting the new default as a separate, intentional change.
        defaultOptions: {
            watchQuery: {
                notifyOnNetworkStatusChange: false,
            },
        },
    });
};

type ApolloProviderProps = {
    children: ReactNode;
};

export const ApolloProvider: FC<ApolloProviderProps> = (props) => {
    const { children } = props;
    const user = useUserEmailIfLoggedIn();
    const prevClientRef = useRef<ApolloClient | null>(null);

    const graphqlClient = useMemo(() => {
        if (prevClientRef.current) {
            prevClientRef.current.stop();
        }

        const newClient = createClient(user);
        prevClientRef.current = newClient;

        return newClient;
    }, [user]);

    return <Provider client={graphqlClient}>{children}</Provider>;
};
